import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { useAuth } from './contexts/AuthContext';

// Layout components
import Header from './components/layout/Header';
import BackgroundApplier from './components/layout/BackgroundApplier';
import ConditionalFooter from './components/layout/ConditionalFooter';

// Auth components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import EmailConfirmation from './components/auth/EmailConfirmation';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import StartSession from './components/auth/StartSession';

// Profile components
import ProfileView from './components/profile/ProfileView';
import ProfileEdit from './components/profile/ProfileEdit';
import UserProfileView from './components/profile/UserProfileView';
import BlockedUsersList from './components/profile/BlockedUsersList';
import FavoritedUsersList from './components/profile/FavoritedUsersList';
import FavoriteMeList from './components/profile/FavoriteMeList';
import ViewMeList from './components/profile/ViewMeList';
import MyViewsList from './components/profile/MyViewsList';

// Photo components
import PhotoGallery from './components/photos/PhotoGallery';
import PhotoUpload from './components/photos/PhotoUpload';

// Search components
import SearchForm from './components/search/SearchForm';
import SearchResults from './components/search/SearchResults';

// Message components
import MessageList from './components/messages/MessageList';
import MessageView from './components/messages/MessageView';

// Billing components
import CreditPurchase from './components/billing/CreditPurchase';

// Settings components
import Settings from './components/settings/Settings';

// Home and other components
import Home from './components/Home';
import NotFound from './components/NotFound';
import About from './components/pages/About';
import Careers from './components/pages/Careers';
import Press from './components/pages/Press';
import Blog from './components/pages/Blog';
import Help from './components/pages/Help';
import Safety from './components/pages/Safety';
import Community from './components/pages/Community';
import Contact from './components/pages/Contact';
import Terms from './components/pages/Terms';
import Privacy from './components/pages/Privacy';
import Cookies from './components/pages/Cookies';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // If still loading auth state, show nothing yet
  if (loading) {
    return <div className="d-flex justify-content-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
  }

  // If not authenticated after loading completes, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

function App() {
  return (
    <div className="d-flex flex-column min-vh-100">
      <Header />
      <BackgroundApplier />
      <Container className="flex-grow-1 py-4">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/press" element={<Press />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/help" element={<Help />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/community" element={<Community />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/confirm-email/:code/:email" element={<EmailConfirmation />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset/:token/:email" element={<ResetPassword />} />
          <Route path="/start-session/:token/:email" element={<StartSession />} />

          {/* Protected routes */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfileView />
            </ProtectedRoute>
          } />
          <Route path="/profile/edit" element={
            <ProtectedRoute>
              <ProfileEdit />
            </ProtectedRoute>
          } />
          <Route path="/photos" element={
            <ProtectedRoute>
              <PhotoGallery />
            </ProtectedRoute>
          } />
          <Route path="/photos/upload" element={
            <ProtectedRoute>
              <PhotoUpload />
            </ProtectedRoute>
          } />
          <Route path="/search" element={
            <ProtectedRoute>
              <SearchForm />
            </ProtectedRoute>
          } />
          <Route path="/search/results" element={
            <ProtectedRoute>
              <SearchResults />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              <MessageList />
            </ProtectedRoute>
          } />
          <Route path="/messages/:username" element={
            <ProtectedRoute>
              <MessageView />
            </ProtectedRoute>
          } />
          <Route path="/credits" element={
            <ProtectedRoute>
              <CreditPurchase />
            </ProtectedRoute>
          } />
          <Route path="/users/:username" element={
            <ProtectedRoute>
              <UserProfileView />
            </ProtectedRoute>
          } />
          <Route path="/blocked-users" element={
            <ProtectedRoute>
              <BlockedUsersList />
            </ProtectedRoute>
          } />
          <Route path="/favorited-users" element={
            <ProtectedRoute>
              <FavoritedUsersList />
            </ProtectedRoute>
          } />
          <Route path="/favorite-me" element={
            <ProtectedRoute>
              <FavoriteMeList />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/view-me" element={
            <ProtectedRoute>
              <ViewMeList />
            </ProtectedRoute>
          } />
          <Route path="/my-views" element={
            <ProtectedRoute>
              <MyViewsList />
            </ProtectedRoute>
          } />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Container>
      <ConditionalFooter />
    </div>
  );
}

export default App;
